/**
 * Utility for managing claim lowers in local storage
 * Format: claimLowers -> { 'address': [{ lowerId: string, executedAt: number }] }
 */

const STORAGE_KEY = 'claimLowers';
const ONE_HOUR_MS = 60 * 60 * 1000;

export interface ClaimingLower {
  lowerId: number;
  executedAt: number;
}

export interface ClaimLowersStorage {
  [address: string]: ClaimingLower[];
}

/**
 * Get all claiming lowers from local storage
 */
export const getClaimLowersStorage = (): ClaimLowersStorage => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Error reading claim lowers from storage:', error);
    return {};
  }
};

/**
 * Save claiming lowers to local storage
 */
const saveClaimLowersStorage = (data: ClaimLowersStorage): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Error saving claim lowers to storage:', error);
  }
};

/**
 * Add a claiming lower for a specific address
 */
export const addClaimingLower = (address: string, lowerId: number): void => {
  const storage = getClaimLowersStorage();
  const addressLowers = storage[address] || [];

  // Check if this lowerId already exists for this address
  const exists = addressLowers.some((item) => item.lowerId === lowerId);
  if (exists) {
    return; // Already tracked
  }

  // Add new claiming lower
  addressLowers.push({
    lowerId,
    executedAt: Date.now(),
  });

  storage[address] = addressLowers;
  saveClaimLowersStorage(storage);
};

/**
 * Check if a lower is currently being claimed
 */
export const isLowerClaiming = (address: string, lowerId: number): boolean => {
  const storage = getClaimLowersStorage();
  const addressLowers = storage[address] || [];
  return addressLowers.some((item) => item.lowerId === lowerId);
};

/**
 * Remove a claiming lower for a specific address
 */
export const removeClaimingLower = (address: string, lowerId: number): void => {
  const storage = getClaimLowersStorage();
  const addressLowers = storage[address] || [];

  storage[address] = addressLowers.filter((item) => item.lowerId !== lowerId);

  // Clean up empty arrays
  if (storage[address].length === 0) {
    delete storage[address];
  }

  saveClaimLowersStorage(storage);
};

/**
 * Clean up expired claiming lowers (older than 1 hour) for a specific address
 */
export const cleanupExpiredClaimingLowers = (address: string): void => {
  const storage = getClaimLowersStorage();
  const addressLowers = storage[address] || [];
  const now = Date.now();

  storage[address] = addressLowers.filter((item) => now - item.executedAt < ONE_HOUR_MS);

  // Clean up empty arrays
  if (storage[address].length === 0) {
    delete storage[address];
  }

  saveClaimLowersStorage(storage);
};

/**
 * Clean up all claimed lowers and expired ones for a specific address
 */
export const cleanupClaimedAndExpiredLowers = (
  address: string,
  claimedLowerIds: number[],
): void => {
  const storage = getClaimLowersStorage();
  const addressLowers = storage[address] || [];
  const now = Date.now();

  // Remove claimed lowers and expired ones
  storage[address] = addressLowers.filter(
    (item) => !claimedLowerIds.includes(item.lowerId) && now - item.executedAt < ONE_HOUR_MS,
  );

  // Clean up empty arrays
  if (storage[address].length === 0) {
    delete storage[address];
  }

  saveClaimLowersStorage(storage);
};
